/**
 * Pure helpers for the field-trip flow (dialog + day-grid extra rows).
 *
 * No React, no `@/api` runtime imports — the dialog, the day panel, and
 * the unit tests all share these. Two concerns live here:
 *
 *   1. The request dialog: which calendar days are pickable, how the
 *      per-column quantity inputs map to the wire `groupQuantities`, and
 *      the ≤30 (auto-place) vs 31+ (Support ticket) threshold copy.
 *   2. The day grid: surfacing placed bagged lines whose menu item is
 *      NOT in the school's browse grid (bagged menus are hidden from the
 *      school projection) as editable "extra rows", and rebuilding them
 *      into the submission when a placed day is re-saved — without this,
 *      a day edit would silently DROP the trip meals (a replace submits
 *      the complete set).
 *   3. Milk for those bagged meals (PROD-1244): milk is standalone-derived
 *      platform-wide, and the bagged menu's system milk rows are hidden
 *      with the menu. `withBaggedMilkRows` surfaces them as milk extra
 *      rows and `baggedMilkDerivationGroups` feeds them — with the placed
 *      bagged meals as the meal count and the menu's SKU policy as the
 *      split — to the same `deriveMilkLines` the hot menus use, so a
 *      mixed or bagged-only day counts (and pays) its milk.
 */

import { resolveMenuItemTitle } from '@ordo/constants/menu-item-title'
import { menuMilkSkuWeights } from '@ordo/constants/milk-derivation'

import type {
  FieldTripGroupQuantity,
  FieldTripGroupScope,
  FieldTripMealSplit,
  FieldTripSkippedDay,
} from '@/api/field-trips'
import type { BaggedMenuMilk } from '@/api/school-orders'
import type { Mealtime } from '@/lib/mealtime'

import type { PlacedDayLine } from './already-ordered'
import { dateKey, isWeekday } from './calendar'
import { cartLineKey, type SchoolCartLine } from './cart'
import { type OrderColumn, WHOLE_SCHOOL_COLUMN_ID } from './columns'
import { orderClosedReason, type SchoolDeadlinePolicy } from './deadline'
import type { DerivedMilkMenuItem } from './derived-milk'
import { sortMealtimes } from './mealtime'

// ---------------------------------------------------------------------------
// Review threshold — orders always place; over it, Support is alerted
// ---------------------------------------------------------------------------

/**
 * Daily total above which the server ALSO raises an ALERT ticket for the Ordo
 * team (Sheila, 2026-09-17: no quantity limit; over a certain size, alert
 * Support to review). Mirrors `FIELD_TRIP_ALERT_THRESHOLD` server-side.
 */
export const FIELD_TRIP_ALERT_THRESHOLD = 30
/** @deprecated the cap is gone — kept as an alias for the review threshold. */
export const FIELD_TRIP_AUTO_MAX = FIELD_TRIP_ALERT_THRESHOLD

export interface FieldTripSubmitMode {
  /** Notes ride to the Ordo team on the alert — only offered over the threshold. */
  showNotes: boolean
  /** Helper copy under the total, explaining what submit will do. */
  helper: string
  submitLabel: string
  submitPendingLabel: string
  /** TRUE over the threshold: the Ordo team is alerted to review the volume. */
  alertsSupport: boolean
}

/** What submitting a given daily total will do (orders always place; 31+ also alerts Support). */
export function fieldTripSubmitMode(dailyTotal: number): FieldTripSubmitMode {
  if (dailyTotal > FIELD_TRIP_ALERT_THRESHOLD) {
    return {
      showNotes: true,
      helper: `Meals will be ordered now. Totals over ${FIELD_TRIP_ALERT_THRESHOLD} a day are flagged for the Ordo team to review — add a note if anything is unusual.`,
      submitLabel: 'Place orders',
      submitPendingLabel: 'Placing…',
      alertsSupport: true,
    }
  }
  return {
    showNotes: false,
    helper: 'Meals will be ordered automatically and appear in your Orders view.',
    submitLabel: 'Place orders',
    submitPendingLabel: 'Placing…',
    alertsSupport: false,
  }
}

// ---------------------------------------------------------------------------
// Bagged-menu windows + date-picker blocking
// ---------------------------------------------------------------------------

/** One published bagged-menu window (`AdminSchool.baggedMealtimes` entry). */
export interface BaggedMealtimeWindow {
  mealtime: Mealtime
  /** Inclusive `YYYY-MM-DD` (an ISO datetime is tolerated). */
  startDate: string
  endDate: string
  menuId: string
  /** Derived-milk context; absent/null = the menu includes no milk. */
  milk?: BaggedMenuMilk | null
}

/** The distinct mealtimes with a bagged window, chronological. */
export function availableBaggedMealtimes(windows: readonly BaggedMealtimeWindow[]): Mealtime[] {
  return sortMealtimes(windows.map((w) => w.mealtime))
}

/**
 * The windows the date pickers should honor: the SELECTED mealtimes'
 * windows, or every window when nothing is selected yet (so the pickers
 * stay usable while the user is still choosing meals).
 */
export function windowsForMealtimes(
  windows: readonly BaggedMealtimeWindow[],
  selected: readonly Mealtime[],
): BaggedMealtimeWindow[] {
  if (selected.length === 0) {
    return [...windows]
  }
  const set = new Set(selected)
  return windows.filter((w) => set.has(w.mealtime))
}

/** `YYYY-MM-DD` of a Date's LOCAL wall clock (what the calendar popover hands us). */
export function localDateKey(date: Date): string {
  return dateKey(date.getFullYear(), date.getMonth() + 1, date.getDate())
}

export interface FieldTripDayDisabledOptions {
  policy: SchoolDeadlinePolicy
  /** Windows already narrowed to the selected mealtimes (`windowsForMealtimes`). */
  windows: readonly BaggedMealtimeWindow[]
  /**
   * Optional NO_SERVICE/holiday probe (e.g. cached day contexts). Days it
   * can't answer for stay pickable — the server pre-validation is the
   * backstop and reports them as blocked/skipped.
   */
  isNoService?: (dayKey: string) => boolean
  /** Injectable for tests; default the real "today"/now. */
  today?: string
  now?: Date
}

/**
 * Whether a calendar day is blocked in the field-trip date pickers:
 * weekends, deadline-closed days (same `orderClosedReason` policy as
 * normal ordering — never skipped), days outside the union of the
 * selected mealtimes' bagged windows, and known NO_SERVICE days.
 */
export function isFieldTripDayDisabled(dayKey: string, opts: FieldTripDayDisabledOptions): boolean {
  if (!isWeekday(dayKey)) {
    return true
  }
  if (orderClosedReason(dayKey, opts.policy, opts.today, opts.now) !== null) {
    return true
  }
  const covered = opts.windows.some((w) => w.startDate.slice(0, 10) <= dayKey && dayKey <= w.endDate.slice(0, 10))
  if (!covered) {
    return true
  }
  return opts.isNoService?.(dayKey) ?? false
}

// ---------------------------------------------------------------------------
// Per-group quantities → wire body
// ---------------------------------------------------------------------------

/**
 * The scope tuple the server gates orders on. Non-AGE_GROUP columns carry
 * their `(groupDimension, groupValue)` identity; AGE_GROUP columns are
 * identified by the LocationGradeRange column id (`columnId`). The
 * whole-school column (NONE campuses) is bucket-less — `group: null`.
 */
export function columnGroupScope(col: OrderColumn): FieldTripGroupScope | null {
  if (col.groupDimension && col.groupValue) {
    return { dimension: col.groupDimension, value: col.groupValue }
  }
  if (col.columnId === WHOLE_SCHOOL_COLUMN_ID) {
    return null
  }
  return { dimension: 'AGE_GROUP', value: col.columnId }
}

/**
 * Map the dialog's per-column quantity inputs to the wire
 * `groupQuantities`. Blank / zero / non-numeric entries are EXCLUDED —
 * only groups actually going ride on the request. Fractions floor.
 */
export function buildGroupQuantities(
  columns: readonly OrderColumn[],
  qtyByColumnId: Readonly<Record<string, string | number>>,
): FieldTripGroupQuantity[] {
  const out: FieldTripGroupQuantity[] = []
  for (const col of columns) {
    const raw = qtyByColumnId[col.columnId]
    const n = typeof raw === 'number' ? raw : typeof raw === 'string' && raw.trim() !== '' ? Number(raw) : 0
    if (!Number.isFinite(n)) {
      continue
    }
    const quantity = Math.floor(n)
    if (quantity <= 0) {
      continue
    }
    out.push({ group: columnGroupScope(col), quantity })
  }
  return out
}

// ---------------------------------------------------------------------------
// Per-meal quantities (a day with more than one field-trip meal)
// ---------------------------------------------------------------------------

/** One field-trip meal a mealtime offers on the picked date. */
export interface FieldTripMealOption {
  menuItemId: string
  title: string
}

/**
 * The meals a mealtime offers on the previewed date, in menu order: live,
 * visible items only, one entry per item id (so a row the menu lists twice
 * can never ask for its quantity twice).
 */
export function fieldTripMealOptions(
  menus: ReadonlyArray<{
    mealtime: string
    items: ReadonlyArray<
      Parameters<typeof resolveMenuItemTitle>[0] & { id: string; visible?: boolean; isArchived?: boolean }
    >
  }>,
  mealtime: Mealtime,
): FieldTripMealOption[] {
  const options = new Map<string, FieldTripMealOption>()
  for (const menu of menus) {
    if (menu.mealtime !== mealtime) {
      continue
    }
    for (const item of menu.items) {
      if (item.visible === false || item.isArchived === true || options.has(item.id)) {
        continue
      }
      options.set(item.id, { menuItemId: item.id, title: resolveMenuItemTitle(item) ?? 'Field-trip meal' })
    }
  }
  return [...options.values()]
}

/** Key of one meal's per-column inputs in the dialog's draft. */
export function fieldTripMealKey(mealtime: Mealtime, menuItemId: string): string {
  return `${mealtime}::${menuItemId}`
}

export interface FieldTripQuantityPlan {
  /** Selected mealtimes offering more than one meal: these take per-meal inputs. */
  splitMealtimes: Mealtime[]
  /** The shared per-group quantities — empty when every mealtime is split. */
  groupQuantities: FieldTripGroupQuantity[]
  mealSplits: FieldTripMealSplit[]
  /** Meals per mealtime: the sum of its meals when split, else the shared total. */
  totals: Partial<Record<Mealtime, number>>
  /** The busiest mealtime's total — what the review threshold applies to. */
  dailyTotal: number
  /** Every selected mealtime has at least one meal. */
  complete: boolean
}

/**
 * Turn the dialog's draft into the request. A mealtime with ONE meal option
 * (or none yet) keeps today's per-group grid; with more, each meal has its
 * own per-group inputs and the mealtime's total is their sum.
 */
export function planFieldTripQuantities(input: {
  mealtimes: readonly Mealtime[]
  optionsByMealtime: Partial<Record<Mealtime, readonly FieldTripMealOption[]>>
  columns: readonly OrderColumn[]
  qtyByColumn: Readonly<Record<string, string | number>>
  qtyByMeal: Readonly<Record<string, Readonly<Record<string, string | number>>>>
}): FieldTripQuantityPlan {
  const mealtimes = sortMealtimes(input.mealtimes)
  const splitMealtimes = mealtimes.filter((mealtime) => (input.optionsByMealtime[mealtime]?.length ?? 0) > 1)
  const shared = buildGroupQuantities(input.columns, input.qtyByColumn)
  const groupQuantities = splitMealtimes.length < mealtimes.length ? shared : []
  const mealSplits: FieldTripMealSplit[] = []
  const totals: Partial<Record<Mealtime, number>> = {}
  for (const mealtime of mealtimes) {
    if (!splitMealtimes.includes(mealtime)) {
      totals[mealtime] = fieldTripDailyTotal(shared)
      continue
    }
    let total = 0
    for (const option of input.optionsByMealtime[mealtime] ?? []) {
      const quantities = buildGroupQuantities(
        input.columns,
        input.qtyByMeal[fieldTripMealKey(mealtime, option.menuItemId)] ?? {},
      )
      if (quantities.length > 0) {
        mealSplits.push({ mealtime, menuItemId: option.menuItemId, groupQuantities: quantities })
        total += fieldTripDailyTotal(quantities)
      }
    }
    totals[mealtime] = total
  }
  const values = mealtimes.map((mealtime) => totals[mealtime] ?? 0)
  return {
    splitMealtimes,
    groupQuantities,
    mealSplits,
    totals,
    dailyTotal: values.length > 0 ? Math.max(...values) : 0,
    complete: values.length > 0 && values.every((value) => value > 0),
  }
}

/** The daily total the 30/31 threshold applies to. */
export function fieldTripDailyTotal(groupQuantities: readonly FieldTripGroupQuantity[]): number {
  return groupQuantities.reduce((sum, g) => sum + g.quantity, 0)
}

/** Normalize the wire `skippedDays` (strings or objects) for display. */
export function normalizeSkippedDays(
  skipped: readonly FieldTripSkippedDay[] | null | undefined,
): Array<{ date: string; reason: string | null }> {
  const out: Array<{ date: string; reason: string | null }> = []
  for (const entry of skipped ?? []) {
    if (typeof entry === 'string') {
      out.push({ date: entry, reason: null })
    } else if (entry && typeof entry.date === 'string') {
      out.push({ date: entry.date, reason: entry.reason ?? null })
    }
  }
  return out
}

// ---------------------------------------------------------------------------
// Day-grid extra rows — placed bagged lines not on the browse grid
// ---------------------------------------------------------------------------

/** Fallback label for a placed line whose payload carries no item name. */
export const EXTRA_ROW_FALLBACK_NAME = 'Bagged lunch'

/** One placed-but-not-browsable item, rendered as an editable grid row. */
export interface ExtraOrderRow {
  menuItemId: string
  name: string
  /**
   * The row is a bagged menu's system milk carrier: its count derives from
   * the bagged MEAL rows (never counts as a meal itself) and its line
   * carries `systemMilk` so the wire stamps DERIVED provenance.
   */
  systemMilk: boolean
  /**
   * A genuine field-trip row (bagged menu or field-trip item) — the only
   * rows the grid badges "Field trip". Other placed lines that are off the
   * grid (e.g. an item since dropped from the menu) still render so day
   * edits round-trip them, but as ordinary rows (PROD-1515).
   */
  fieldTrip: boolean
}

/**
 * The caption under an extra row's name. Only genuine field-trip rows talk
 * about a field trip; any other off-grid line is simply no longer on the
 * day's menu (PROD-1515 — a pre-release line read "Placed for a field trip").
 */
export function extraRowCaption(row: Pick<ExtraOrderRow, 'fieldTrip' | 'systemMilk'>): string {
  if (row.fieldTrip) {
    return row.systemMilk
      ? 'Milk for the field-trip meals — follows their counts'
      : "Placed for a field trip — not on this day's menu"
  }
  return "Already ordered — no longer on this day's menu"
}

/**
 * The placed lines whose menu item is NOT in the browse grid, grouped by
 * mealtime and deduped by item — mostly field-trip bagged lines (their
 * menus are hidden from the school projection), which must still render +
 * edit like normal rows so day edits round-trip them. Off-grid is NOT proof
 * of a field trip: each row carries the line's own `fieldTrip` flag.
 */
export function extraFieldTripRows(
  placedLines: readonly PlacedDayLine[],
  itemsByMealtime: ReadonlyMap<Mealtime, ReadonlyArray<{ id: string }>>,
): Map<Mealtime, ExtraOrderRow[]> {
  const out = new Map<Mealtime, ExtraOrderRow[]>()
  for (const line of placedLines) {
    const gridItems = itemsByMealtime.get(line.mealtime) ?? []
    if (gridItems.some((it) => it.id === line.menuItemId)) {
      continue
    }
    const rows = out.get(line.mealtime) ?? []
    const existing = rows.find((r) => r.menuItemId === line.menuItemId)
    if (existing) {
      existing.fieldTrip ||= line.fieldTrip
      // A later line may carry the name an earlier one lacked.
      if (existing.name === EXTRA_ROW_FALLBACK_NAME && line.itemName) {
        existing.name = line.itemName
      }
      continue
    }
    rows.push({
      menuItemId: line.menuItemId,
      name: line.itemName ?? EXTRA_ROW_FALLBACK_NAME,
      systemMilk: line.systemMilk === true,
      fieldTrip: line.fieldTrip,
    })
    out.set(line.mealtime, rows)
  }
  return out
}

/**
 * The bagged menus whose window covers `dayKey` at each mealtime — the
 * menus the placed field-trip lines came from. Falls back to every window
 * of the mealtime when none covers the day (a window edited after the
 * placement), so the milk context is never silently lost.
 */
export function baggedWindowsForDay(
  windows: readonly BaggedMealtimeWindow[],
  dayKey: string,
): Map<Mealtime, BaggedMealtimeWindow[]> {
  const byMealtime = new Map<Mealtime, BaggedMealtimeWindow[]>()
  for (const window of windows) {
    const list = byMealtime.get(window.mealtime) ?? []
    list.push(window)
    byMealtime.set(window.mealtime, list)
  }
  for (const [mealtime, list] of byMealtime) {
    const covering = list.filter((w) => w.startDate.slice(0, 10) <= dayKey && dayKey <= w.endDate.slice(0, 10))
    if (covering.length > 0) {
      byMealtime.set(mealtime, covering)
    }
  }
  return byMealtime
}

/**
 * Add the bagged menus' system milk carriers as MILK extra rows on every
 * mealtime that holds a bagged MEAL row. A day placed before PROD-1244 has
 * bagged meals and no milk lines at all; surfacing the carriers (at 0) is
 * what lets Edit derive and save the missing milk. Carriers already placed
 * (and so already extra rows) are marked, not duplicated. Bagged windows
 * without a milk context (`milk` null — the menu includes no milk) add
 * nothing.
 */
export function withBaggedMilkRows(
  rows: ReadonlyMap<Mealtime, readonly ExtraOrderRow[]>,
  windows: readonly BaggedMealtimeWindow[],
  dayKey: string,
): Map<Mealtime, ExtraOrderRow[]> {
  const out = new Map<Mealtime, ExtraOrderRow[]>()
  for (const [mealtime, list] of rows) {
    out.set(
      mealtime,
      list.map((row) => ({ ...row })),
    )
  }
  const windowsByMealtime = baggedWindowsForDay(windows, dayKey)
  for (const [mealtime, list] of out) {
    const carriers = (windowsByMealtime.get(mealtime) ?? []).flatMap((w) => w.milk?.carriers ?? [])
    if (carriers.length === 0) {
      continue
    }
    const carrierIds = new Set(carriers.map((c) => c.menuItemId))
    for (const row of list) {
      if (carrierIds.has(row.menuItemId)) {
        row.systemMilk = true
        row.fieldTrip = true
      }
    }
    // Only a FIELD-TRIP meal row pulls in the bagged carriers — an ordinary
    // off-grid line has nothing to do with the bagged menu's milk.
    const hasMealRow = list.some((row) => !row.systemMilk && row.fieldTrip)
    if (!hasMealRow) {
      continue
    }
    for (const carrier of carriers) {
      if (!list.some((row) => row.menuItemId === carrier.menuItemId)) {
        list.push({ menuItemId: carrier.menuItemId, name: carrier.name, systemMilk: true, fieldTrip: true })
      }
    }
  }
  return out
}

/** One bagged menu's milk derivation input for a mealtime. */
export interface BaggedMilkGroup {
  mealtime: Mealtime
  /** The bagged MEAL rows (as BUNDLE meals) + the menu's milk carriers. */
  items: DerivedMilkMenuItem[]
  /** The menu's SKU policy inputs (`menuMilkSkuWeights`). */
  policy: Pick<BaggedMenuMilk, 'selectionSource' | 'milkOptionCodes' | 'carriers'>
}

/**
 * Per mealtime, the derivation group for the placed bagged meals: every
 * non-milk extra row counts as ONE meal per unit (a bagged bundle IS the
 * meal), and the covering bagged menu's carriers are the milk rows. Empty
 * when the mealtime has no bagged meal rows or no milk context. Feed the
 * result to `deriveMilkLines` with `baggedMilkShareFor` as `milkShareFor`.
 */
export function baggedMilkDerivationGroups(opts: {
  extraRowsByMealtime: ReadonlyMap<Mealtime, readonly ExtraOrderRow[]>
  windows: readonly BaggedMealtimeWindow[]
  dayKey: string
}): Map<Mealtime, BaggedMilkGroup> {
  const out = new Map<Mealtime, BaggedMilkGroup>()
  const windowsByMealtime = baggedWindowsForDay(opts.windows, opts.dayKey)
  for (const [mealtime, rows] of opts.extraRowsByMealtime) {
    const milkWindows = (windowsByMealtime.get(mealtime) ?? []).filter((w) => w.milk && w.milk.carriers.length > 0)
    const primary = milkWindows[0]?.milk
    if (!primary) {
      continue
    }
    const carriers = milkWindows.flatMap((w) => w.milk?.carriers ?? [])
    const carrierIds = new Set(carriers.map((c) => c.menuItemId))
    const mealRows = rows.filter((row) => row.fieldTrip && !row.systemMilk && !carrierIds.has(row.menuItemId))
    if (mealRows.length === 0) {
      continue
    }
    const items: DerivedMilkMenuItem[] = [
      ...mealRows.map((row) => ({
        id: row.menuItemId,
        itemKind: 'BUNDLE' as const,
        componentKind: null,
        lineupMilkOptionCode: null,
        skuComponentId: null,
        name: row.name,
        imageUri: null,
        unitPriceCents: null,
        currency: null,
      })),
      ...carriers.map((carrier) => ({
        id: carrier.menuItemId,
        itemKind: 'COMPONENT' as const,
        componentKind: 'BEVERAGE' as const,
        lineupMilkOptionCode: carrier.milkOptionCode ?? 'UNKNOWN',
        skuComponentId: carrier.componentId,
        name: carrier.name,
        imageUri: null,
        unitPriceCents: null,
        currency: null,
      })),
    ]
    out.set(mealtime, {
      mealtime,
      items,
      policy: { selectionSource: primary.selectionSource, milkOptionCodes: primary.milkOptionCodes, carriers },
    })
  }
  return out
}

/**
 * The `milkShareFor` a bagged group derives with: the menu's SKU policy
 * (`menuMilkSkuWeights` — explicit MENU selection wins, else the campus
 * shares) re-keyed by SKU Component id, which is how `deriveMilkLines`
 * looks weights up. Carriers without a Component id fall through to the
 * derivation's even-weight default.
 */
export function baggedMilkShareFor(
  group: BaggedMilkGroup,
  campusShareFor: (mealtime: Mealtime | null, columnId: string) => Record<string, number>,
): (mealtime: Mealtime | null, columnId: string) => Record<string, number> {
  return (mealtime, columnId) => {
    const weights = menuMilkSkuWeights({
      milkSelectionSource: group.policy.selectionSource,
      menuMilkOptionCodes: group.policy.milkOptionCodes,
      carriers: group.policy.carriers,
      campusShareByComponentId: campusShareFor(mealtime, columnId),
    })
    const byComponentId: Record<string, number> = {}
    for (const carrier of group.policy.carriers) {
      if (carrier.componentId !== null) {
        byComponentId[carrier.componentId] = weights[carrier.menuItemId] ?? 0
      }
    }
    return byComponentId
  }
}

/**
 * Rebuild the extra rows' edited quantities into cart lines for a placed
 * day's re-save. `handleSaveEdit` submits the COMPLETE set per mealtime
 * (a replace, not a delta) — omitting these would drop the trip meals.
 * Lines carry no price snapshot (the browse grid never priced them); the
 * server re-resolves and its reprice guard still protects the total.
 */
export function buildExtraRowLines(opts: {
  locationId: string
  dateKey: string
  columns: readonly OrderColumn[]
  extraRowsByMealtime: ReadonlyMap<Mealtime, readonly ExtraOrderRow[]>
  qtyFor: (mealtime: Mealtime, menuItemId: string, columnId: string) => number
  /**
   * Whether a milk extra row's cell still tracks the derivation — the line
   * then keeps `suggested`, so the wire stamps `quantitySource: DERIVED`
   * and the NEXT edit re-derives it. Omitted = every cell was typed.
   */
  isDerived?: (key: string) => boolean
}): SchoolCartLine[] {
  const lines: SchoolCartLine[] = []
  for (const mealtime of sortMealtimes(opts.extraRowsByMealtime.keys())) {
    for (const row of opts.extraRowsByMealtime.get(mealtime) ?? []) {
      for (const col of opts.columns) {
        const quantity = opts.qtyFor(mealtime, row.menuItemId, col.columnId)
        if (quantity <= 0) {
          continue
        }
        const key = cartLineKey(opts.dateKey, mealtime, col.columnId, row.menuItemId)
        lines.push({
          key,
          locationId: opts.locationId,
          date: opts.dateKey,
          mealtime,
          columnId: col.columnId,
          gradeRangeId: col.gradeRangeId,
          groupDimension: col.groupDimension,
          groupValue: col.groupValue,
          menuItemId: row.menuItemId,
          name: row.name,
          imageUri: null,
          unitPriceCents: null,
          currency: null,
          quantity,
          // System-milk identity + derivation state — the wire stamps
          // DERIVED off `systemMilk && suggested` (`isDerivedQuantity`).
          ...(row.systemMilk ? { systemMilk: true } : {}),
          ...(row.systemMilk && opts.isDerived?.(key) ? { suggested: true } : {}),
        })
      }
    }
  }
  return lines
}
